import { createContext, ReactElement, useReducer } from "react"
import axios from "utils/axios"
import {
  FETCH_COMPANIES,
  FETCH_COMPANY_BREAKDOWNS,
  FETCH_PAYMENT_METHODS,
  GET_USER,
  GET_USER_INFORMATION
} from "../store/reducers/actions"
import { mapPaginatedDataToCamelCase, mapToCamelCase, mapToSnakeCase } from "../utils/caseTransformation"
import { PaginatedData } from "types/root"
import { CompaniesContextType, Company, CompanyBreakDown } from "types/companies"
import artistsKahunaReducer, { initialArtistsKahunaState } from "store/reducers/artists-kahuna"
import { ArtistKahunaUser, ArtistsKahunaContextType, PaymentMethod } from "types/artists-kahuna"
import { toastMessage } from "utils"
import { AccountCreationForm } from "pages/offers/deals/account-information/AccountCreate"
import { UserInformation } from "pages/offers/deals/user-information/UserInformation"

const ArtistsKahunaContext = createContext<ArtistsKahunaContextType | null>(null)

export const ArtistsKahunaProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(artistsKahunaReducer, initialArtistsKahunaState)

  const sendCodeValidation = async (email: string, isLogin: boolean) => {
    try {
      const response = await axios("/artists-kahuna/send_code_validation/", {
        method: "post",
        data: { email, user_check: isLogin ? "1" : "0" }
      })

      if (!response.data.success) {
        toastMessage(response.data.message, "error")
        return false
      }

      toastMessage(response.data.message, "success")
      return true
    } catch (error) {
      console.error("Failed to send code validation:", error)
      toastMessage("Error sending verification code!", "error")
      return false
    }
  }

  const authenticateAndCreateAccount = async (form: AccountCreationForm, artistSpotifyId: string) => {
    try {
      const response = await axios("/artists-kahuna/authenticate_create_account/", {
        method: "post",
        data: { ...mapToSnakeCase({ ...form, artistSpotifyId }) },
        withCredentials: true
      })

      if (!response.data.success) {
        toastMessage(response.data.message, "error", 10000)
        return false
      }

      toastMessage(response.data.message, "success")
      return true
    } catch (error) {
      console.error("Failed to authenticate and create account:", error)
      toastMessage("Error creating account!", "error")
      return false
    }
  }

  const fetchPaymentMethodList = async () => {
    try {
      const response = await axios(`/artists-kahuna/user_payment_methods/`, {
        method: "get",
        withCredentials: true
      })

      dispatch({
        type: FETCH_PAYMENT_METHODS,
        payload: {
          ...state,
          paginatedPaymentMethodList: mapPaginatedDataToCamelCase<PaginatedData<PaymentMethod>>(response.data)
        }
      })

      return response.data.status
    } catch (error) {
      console.error("Failed to fetch payment methods:", error)
      return 500
    }
  }

  const createPaymentMethod = async (form: PaymentMethod) => {
    try {
      const response = await axios(`/artists-kahuna/create_payment_method/`, {
        method: "post",
        data: { ...mapToSnakeCase(form) },
        withCredentials: true
      })

      if (!response.data.success) {
        toastMessage("Error creating payment method!", "error")
        return false
      }
      toastMessage("Successfully created payment method!", "success")
      fetchPaymentMethodList()
      return true
    } catch (error) {
      console.error("Failed to create payment method:", error)
      toastMessage("Error creating payment method!", "error")
      return false
    }
  }

  const updatePaymentMethod = async (form: PaymentMethod) => {
    try {
      const response = await axios(`/artists-kahuna/update_payment_method/`, {
        method: "put",
        data: { ...mapToSnakeCase(form) },
        withCredentials: true
      })

      if (!response.data.success) {
        toastMessage("Error updating payment method!", "error")
        return false
      }
      toastMessage("Successfully updated payment method!", "success")
      fetchPaymentMethodList()
      return true
    } catch (error) {
      console.error("Failed to update payment method:", error)
      toastMessage("Error updating payment method!", "error")
      return false
    }
  }

  const deletePaymentMethod = async (paymentMethodId: number) => {
    try {
      const response = await axios(`/artists-kahuna/${paymentMethodId}/delete_payment_method/`, {
        method: "delete",
        withCredentials: true
      })

      if (!response.data.success) {
        toastMessage("Error deleting payment method!", "error")
        return false
      }
      toastMessage("Successfully deleted payment method!", "success")
      fetchPaymentMethodList()
      return true
    } catch (error) {
      console.error("Failed to delete payment method:", error)
      toastMessage("Error deleting payment method!", "error")
      return false
    }
  }

  const getUserInformation = async () => {
    try {
      const response = await axios(`/artists-kahuna/user_information/`, {
        method: "get",
        withCredentials: true
      })

      dispatch({
        type: GET_USER_INFORMATION,
        payload: {
          ...state,
          userInformation: response.data.result
        }
      })

      return response.data.status
    } catch (error) {
      console.error("Failed to get user information:", error)
      return 500
    }
  }

  const saveUserInformation = async (form: UserInformation) => {
    try {
      const response = await axios(`/artists-kahuna/save_user_information/`, {
        method: "post",
        data: { ...mapToSnakeCase(form) },
        withCredentials: true
      })

      if (!response.data.success) {
        toastMessage("Error saving user information!", "error")
        return false
      }
      toastMessage("Successfully saved user information!", "success")
      return true
    } catch (error) {
      console.error("Failed to save user information:", error)
      toastMessage("Error saving user information!", "error")
      return false
    }
  }

  const getUser = async () => {
    try {
      const response = await axios(`/artists-kahuna/user/`, {
        method: "get",
        withCredentials: true
      })

      if (!response.data.result) {
        return response.data.status
      }

      dispatch({
        type: GET_USER,
        payload: {
          ...state,
          artistKahunaUser: mapToCamelCase(response.data.result) as ArtistKahunaUser
        }
      })

      return response.data.status
    } catch (error) {
      console.error("Failed to get user:", error)
      return 500
    }
  }

  const updateUserTakedownStatus = async (artistUploadStatus: string, hasAssets: boolean = true) => {
    try {
      const response = await axios(`/artists-kahuna/update_user_takedown_status/`, {
        method: "put",
        data: { artist_upload_status: artistUploadStatus, has_assets: hasAssets },
        withCredentials: true
      })

      if (!response.data.success) {
        toastMessage("Error! Please contact costomer support.", "error")
        return false
      }
      toastMessage("Successful!", "success")
      return true
    } catch (error) {
      console.error("Failed to update user takedown status:", error)
      toastMessage("Error! Please contact costomer support.", "error")
      return false
    }
  }

  return (
    <ArtistsKahunaContext.Provider
      value={{
        ...state,
        sendCodeValidation,
        authenticateAndCreateAccount,
        fetchPaymentMethodList,
        createPaymentMethod,
        updatePaymentMethod,
        deletePaymentMethod,
        getUserInformation,
        saveUserInformation,
        getUser,
        updateUserTakedownStatus
      }}
    >
      {children}
    </ArtistsKahunaContext.Provider>
  )
}

export default ArtistsKahunaContext
